<template>
	<v-dialog
		:model-value="$props.dialogVisible"
		:aria-labelledby="dialogTitleId"
		@update:model-value="onOverlayModelValueUpdate"
		:max-width="$props.options?.width"
		:width="display.smAndDown.value ? '90%' : ($props.options?.width ?? '80%')"
		:z-index="$props.options?.zIndex || 9000"
		:style="{ zIndex: $props.options?.zIndex || 9000 }"
		:content-class="dialogContentClass"
		:contained="$props.options?.contained"
		:opacity="$props.options?.opacity"
		:scrim="$props.options?.scrim"
		:persistent="$props.persistentDialog"
		@keydown.esc="handleOutsideClick"
		@click:outside="handleOutsideClick"
		@keydown.enter="onDialogEnterKeydown"
		@after-leave="onAfterLeave">
		<v-card v-test="'dialog'" class="pa-2 dialog-container w-100" rounded="xl" elevation="8">
			<v-card-title
				class="text-h5 pa-4 dialog-header"
				:style="{ 'word-break': 'normal', 'white-space': 'normal' }"
				:class="{ 'shadow-bottom': hasScrolledDown }">
				<h5 v-if="$props.title || !$slots['dialog-header']" :id="dialogTitleId" v-test="'dialog-title'">{{ $props.title }}</h5>
				<slot name="dialog-header" :title-id="dialogTitleId">
					<!-- for more complex displays -->
				</slot>
			</v-card-title>
			<div
				ref="dialogBodyContainer"
				class="dialog-body-container"
				:class="display.smAndDown.value && isLandscape ? 'landscape-dialog-body-container' : ''"
				@scroll="handleScroll">
				<v-card-text v-test="'dialog-message'" class="pa-4">
					<span v-html="sanitizedMessage"></span>
					<slot name="dialog-body">
						<!-- for more complex displays -->
					</slot>
				</v-card-text>
			</div>
			<v-card-actions
				v-if="!$props.removeFooter"
				class="pa-4 pb-2 d-flex align-center flex-wrap ga-2 dialog-footer"
				:class="{
					'shadow-top': hasScrolledUp,
					'justify-space-between': $props.customButton,
					'two-default-actions': !$props.customButton && !$props.removeCancelButton && !$props.removeAgreeButton,
					'justify-end': !$props.customButton
				}">
				<v-btn
					v-if="!$props.removeCancelButton"
					id="cancel-action-btn"
					v-test="'dialog-cancel-button'"
					v-track="'dialog-cancel-button'"
					:class="$props.primaryCancelButton ? '' : 'white-bg'"
					rounded="xl"
					elevation="0"
					:prepend-icon="$props.customCancelIcon"
					:color="$props.primaryCancelButton ? 'primary' : 'grey'"
					:variant="$props.primaryCancelButton ? 'flat' : $props.customButton || $props.removeAgreeButton ? 'text' : 'outlined'"
					:disabled="$props.agreeLoading"
					@click="cancel">
					{{ $props.cancelText }}
				</v-btn>
				<v-spacer v-if="$props.customButton" />
				<v-btn
					v-if="$props.customButton && $props.customButtonBeforeAgree"
					id="custom-action-btn"
					v-test="'dialog-custom-button'"
					v-track="'dialog-custom-button'"
					:color="$props.customButtonColor"
					:variant="$props.customButtonVariant"
					class="flex-grow-0 flex-shrink-0"
					rounded="xl"
					elevation="0"
					:disabled="$props.disableCustomButton"
					@click="customAction">
					{{ $props.customText }}
				</v-btn>
				<v-btn
					v-if="!$props.removeAgreeButton"
					id="confirm-action-btn"
					v-test="'dialog-agree-button'"
					v-track="'dialog-agree-button'"
					rounded="xl"
					:prepend-icon="$props.customAgreeIcon"
					elevation="0"
					:color="$props.isAngry ? 'error' : 'primary'"
					variant="flat"
					class="flex-grow-0 flex-shrink-0"
					:disabled="$props.disableAgreeButton"
					:loading="$props.agreeLoading"
					@click="agree">
					{{ $props.agreeText }}
				</v-btn>
				<v-btn
					v-if="$props.customButton && !$props.customButtonBeforeAgree"
					id="custom-action-btn"
					v-test="'dialog-custom-button'"
					v-track="'dialog-custom-button'"
					:color="$props.customButtonColor"
					:variant="$props.customButtonVariant"
					class="flex-grow-0 flex-shrink-0"
					rounded="xl"
					elevation="0"
					:disabled="$props.disableCustomButton"
					@click="customAction">
					{{ $props.customText }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts" setup>
	import { ref, nextTick, watch, computed, useId } from "vue";
	import { blurActiveElement } from "@/helpers/utilities";
	import { useDisplay } from "vuetify";
	import { sanitizeDialog } from "@/helpers/htmlSanitize";
	import { useOrientationChange } from "@/composables/useOrientationChange";

	let hasScrolledDown = ref<boolean>(false);
	let hasScrolledUp = ref<boolean>(false);
	const dialogBodyContainer = ref<HTMLElement | null>(null);
	const isLandscape = ref(window.matchMedia("(orientation: landscape)").matches);
	const display = useDisplay();
	const { orientationChangeCount } = useOrientationChange();
	const dialogTitleId = `dialog-title-${useId()}`;

	const props = defineProps({
		dialogVisible: {
			type: Boolean,
			default: false,
			required: true
		},
		title: {
			type: String
		},
		message: {
			type: String
		},
		options: {
			type: Object,
			default: () => ({
				width: "500px"
			})
		},
		isAngry: {
			type: Boolean,
			default: false
		},
		removeCancelButton: {
			type: Boolean,
			default: false
		},
		removeAgreeButton: {
			type: Boolean,
			default: false
		},
		agreeText: {
			type: String,
			default: "OK"
		},
		cancelText: {
			type: String,
			default: "Cancel"
		},
		disableAgreeButton: {
			type: Boolean,
			default: false
		},
		agreeLoading: {
			type: Boolean,
			default: false
		},
		persistentDialog: {
			type: Boolean,
			default: false
		},
		customButton: {
			type: Boolean,
			default: false
		},
		customText: {
			type: String,
			default: "Save As"
		},
		disableCustomButton: {
			type: Boolean,
			default: false
		},
		customOutsideClickFunction: {
			type: Function,
			default: null
		},
		customButtonVariant: {
			type: String,
			default: "flat"
		},
		customButtonColor: {
			type: String,
			default: "primary"
		},
		customButtonBeforeAgree: {
			type: Boolean,
			default: false
		},
		primaryCancelButton: {
			type: Boolean,
			default: false
		},
		customCancelIcon: {
			type: String,
			default: ""
		},
		customAgreeIcon: {
			type: String,
			default: ""
		},
		removeFooter: {
			type: Boolean,
			default: false
		}
	});
	const emit = defineEmits(["cancel", "agree", "customAction", "afterLeave"]);

	const sanitizedMessage = computed(() => sanitizeDialog(props.message ?? ""));

	const dialogContentClass = computed(() => {
		const classNames: string[] = [];

		if (props.options?.fullHeight) {
			classNames.push("full-height");
		}
		if (props.options?.contentClass) {
			classNames.push(props.options.contentClass);
		}
		return classNames.join(" ");
	});

	// Also bound to @keydown.esc (LSP-17844): persistentDialog means "outside-click AND Escape
	// won't dismiss it", matching Vuetify's own `persistent` prop semantics. Escape previously ran
	// `(customOutsideClickFunction || cancel)()` with no persistent check, so it could dismiss a
	// dialog a consumer had explicitly marked persistent.
	//
	// Actual blast radius is narrower than the list of persistent consumers, because that
	// pre-fix expression had two escape hatches of its own. Of the five components binding
	// `:persistent-dialog="true"`, only TWO changed behaviour:
	//   - MergeContactDialog (destructive merge confirmation) and SheetEditor (in-progress edits):
	//     no override and a real @cancel listener, so Escape genuinely dismissed them.
	//   - NoAccount and FormUpgradeBlockedDialog already passed a no-op override
	//     (`ignoreOutsideClick` / `noop`), so Escape resolved to the no-op, not cancel() —
	//     already inert.
	//   - WelcomeWizardDialog binds no @cancel listener at all, so its emitted `cancel` went
	//     nowhere — also already inert.
	// ConfirmDialog forwards the prop from its caller, so it is affected wherever a caller opts in
	// AND does not pass its own no-op override. The guard is still correct for all of them; it just
	// only *changes* anything for the first two.
	const handleOutsideClick = () => {
		if (props.persistentDialog) return;
		(props.customOutsideClickFunction || cancel)();
	};
	const cancel = () => {
		emit("cancel");
		nextTick(() => {
			blurActiveElement();
		});
	};
	const agree = () => {
		emit("agree");
	};
	const customAction = () => {
		emit("customAction");
	};
	const onAfterLeave = () => {
		emit("afterLeave");
	};
	// `scrollTopOverride` exists for the reopen path below. `.dialog-body-container` sets
	// `scroll-behavior: smooth`, so writing `scrollTop = 0` starts an ANIMATED scroll rather than
	// jumping — per CSSOM-View a real browser may not have applied it by the time the next line
	// reads `scrollTop` back, which would compute both shadows from the PRE-reset offset. The
	// reopen path therefore passes the offset it just set (0) instead of re-reading it, so the
	// recompute is deterministic and cannot race the smooth-scroll animation. The scroll handler
	// passes nothing and keeps reading the live offset, which is what it wants.
	// happy-dom/jsdom don't model smooth-scroll timing at all, so no unit test can catch that
	// race — it only reproduces in a real browser.
	function updateScrollShadows(element: HTMLElement, scrollTopOverride?: number) {
		const scrollTop = scrollTopOverride ?? element.scrollTop;
		hasScrolledDown.value = scrollTop > 0;
		hasScrolledUp.value = scrollTop + element.clientHeight < element.scrollHeight;
	}

	// LSP-16532: keep Enter from submitting/closing while typing in a form field, but let it
	// activate a focused action button (Cancel/Delete/etc.) — the previous blanket
	// `@keydown.enter.prevent` cancelled the button's synthesized click, so keyboard users could
	// tab to a button but never trigger it.
	const onDialogEnterKeydown = (event: KeyboardEvent) => {
		const target = event.target as HTMLElement | null;
		if (target?.closest("button")) {
			return;
		}
		event.preventDefault();
	};

	function handleScroll(event: Event) {
		const element = event.target as HTMLElement;
		if (!element) return;
		updateScrollShadows(element);
	}

	const updateOrientation = () => {
		isLandscape.value = window.matchMedia("(orientation: landscape)").matches;
	};

	watch(orientationChangeCount, updateOrientation);

	// LSP-17253: iOS Safari / WebKit only synthesizes a `click` from a tap when the tapped element
	// (or a non-document ancestor) is "clickable" — it has a direct click listener or similar
	// interactive affordance. Vuetify's scrim is a plain <div> with no direct listener, and its
	// ClickOutside directive listens at the DOCUMENT level, which does not count for WebKit's
	// heuristic. Net effect on iPhones: tapping the scrim produced touch/pointer events but never a
	// `click`, so `click:outside` never fired, the dialog could not be dismissed by tapping outside,
	// and Vuetify's `v-overlay-scroll-blocked` scroll lock stayed engaged ("breaks scrolling").
	// Attaching ANY direct click listener to the scrim flips WebKit's clickability heuristic, the
	// tap synthesizes a real `click`, and Vuetify's existing click-outside path then handles the
	// close exactly once — so this handler is deliberately a no-op and no behavior changes on
	// platforms where tap-to-close already worked (verified against Playwright WebKit + Chromium
	// with touch emulation on Vuetify 3.12.6). The persistent-dialog guard in handleOutsideClick
	// still applies because the close still flows through `click:outside`.
	// Visibility is parent-controlled: the parent owns `dialogVisible` and closes via the `cancel`
	// emit. This was previously written as `v-model="$props.dialogVisible"`, whose compiled update
	// handler assigned into $props — a silent no-op that logged "[Vue warn] Set operation on key
	// 'dialogVisible' failed: target is readonly" on every internal Vuetify close attempt
	// (LSP-17253 break-it round 1). The listener must still EXIST: Vuetify's useProxiedModel only
	// treats the model as controlled when BOTH the prop and an `onUpdate:modelValue` listener are
	// present; without it, VDialog would track internal state and could close itself without the
	// parent's consent, desyncing prop and overlay. So: explicit prop + intentional no-op listener.
	const onOverlayModelValueUpdate = () => {};

	const enableWebkitTapClickSynthesisOnScrim = () => {
		const overlayElement = dialogBodyContainer.value?.closest(".v-overlay");
		// No surrounding .v-overlay means this isn't a real mounted v-dialog (e.g. a stubbed test
		// environment) — there is no scrim to instrument, so there is nothing to do or diagnose.
		if (!overlayElement) return;
		const scrimElement = overlayElement.querySelector(":scope > .v-overlay__scrim");
		if (!scrimElement) {
			// Diagnostic per the silent-failure logging rule: a missing scrim means iOS outside-tap
			// close is broken again for this dialog. `scrim: false` consumers legitimately have none.
			if (props.options?.scrim !== false) {
				console.warn("Dialog open: scrim element not found; iOS tap-outside close may not work", {
					title: props.title
				});
			}
			return;
		}
		// Re-adding the same function reference on reopen is a no-op per the DOM spec, and the
		// listener is garbage-collected with the scrim element itself — no cleanup needed.
		scrimElement.addEventListener("click", webkitScrimTapClickSynthesisNoop);
	};
	// Named no-op: its only job is existing as a DIRECT listener on the scrim so WebKit treats the
	// scrim as clickable and synthesizes `click` from taps (see comment above).
	const webkitScrimTapClickSynthesisNoop = () => {};

	// Generation token for the reopen watcher below. A plain `props.dialogVisible` re-check
	// after the await can't tell two DIFFERENT "true" invocations apart from each other when
	// the prop round-trips true->false->true (etc.) multiple times before either one's
	// nextTick resolves — both would see the same final `true` value. Each true-transition
	// claims the next generation number; only the invocation whose captured generation still
	// matches the latest one when it resumes is allowed to act (LSP-17276).
	let reopenGeneration = 0;

	watch(
		() => props.dialogVisible,
		async (newVal) => {
			if (!newVal) return;
			const generation = ++reopenGeneration;
			// Do NOT gate on dialogBodyContainer.value here: after a real close (once
			// v-dialog's leave transition finishes), the previous body unmounts and the
			// ref goes null before this callback runs. Requiring it truthy up front would
			// bail out permanently, even though v-dialog mounts a fresh container moments
			// later — leaving the scroll position and shadows stuck from the prior session
			// (LSP-17276). Wait a tick for the (re)mount, then check.
			await nextTick();
			// Every early return below skips the scroll-shadow recompute silently. Each one exists
			// because a break-it round found a real race here, and diagnosing those rounds meant
			// reconstructing WHICH guard fired from indirect signals. Log one line per fail-condition
			// so a future recurrence is a log read rather than the same archaeology (per the repo's
			// silent-failure logging rule).
			// Superseded by a later reopen that also resolved to true? Let that one own it.
			if (generation !== reopenGeneration) {
				console.warn("Dialog reopen: superseded by a newer reopen; skipping scroll-shadow recompute", {
					generation,
					latestGeneration: reopenGeneration
				});
				return;
			}
			// Re-check dialogVisible itself: if a subsequent close superseded this
			// invocation while it was suspended on the tick above (e.g. the dialog was
			// reopened and immediately closed again before the leave transition of an
			// even earlier close finished), this stale invocation must not reset the
			// scroll position or shadows out from under the session that is now closing.
			if (!props.dialogVisible) {
				console.warn("Dialog reopen: dialog closed again while awaiting nextTick; skipping scroll-shadow recompute", {
					generation
				});
				return;
			}
			// Read the ref once into a local: the assignment below can itself trigger
			// synchronous side effects (e.g. a scroll listener reacting to the reset),
			// and re-reading dialogBodyContainer.value afterward could see it go null.
			const container = dialogBodyContainer.value;
			if (!container) {
				console.warn("Dialog reopen: body container ref was null after nextTick; skipping scroll-shadow recompute", {
					generation
				});
				return;
			}
			// The overlay (and its scrim) is mounted by now — make iOS taps on the scrim synthesize
			// real clicks so tap-outside close works (LSP-17253; see the function's comment).
			enableWebkitTapClickSynthesisOnScrim();
			container.scrollTop = 0;
			// The scrollTop write above is the last point this container could be detached
			// out from under us; don't trust a detached node's metrics for the recompute.
			if (!container.isConnected) {
				console.warn("Dialog reopen: body container detached during scroll reset; skipping scroll-shadow recompute", {
					generation
				});
				return;
			}
			// Recompute from the live DOM metrics of the reopened content rather than
			// hardcoding both flags to false — at scrollTop 0, overflowing content still
			// needs its "more content below" shadow (LSP-17276). Pass the offset we just set
			// rather than re-reading it; see updateScrollShadows for why (smooth-scroll race).
			updateScrollShadows(container, 0);
		},
		// `immediate` so a dialog mounted with dialogVisible already true is treated as an open too:
		// without it the scrim never gets its WebKit click-synthesis listener (LSP-17253 break-it
		// round 1) and the initial scroll/shadow state is never computed. The handler's own guards
		// (generation token, visibility re-check, container null-check) make the immediate run safe
		// when the dialog mounts closed — it exits at the `if (!newVal) return`.
		{ immediate: true }
	);
</script>

<style css scoped>
	.v-btn.primary.delete-btn {
		background-color: var(--v-error-base) !important;
		color: #fff !important;
	}
	.dialog-header,
	.dialog-footer {
		background-color: #fff;
	}

	/* Always split the two default action buttons left/right when there's room — matches the
	   left-aligned title/text above. flex-wrap (set on .dialog-footer in the template) plus this
	   minimum gap is what decides "is there room": if both buttons plus this gap don't fit on one
	   line, they wrap onto two rows (each flush-left) with adequate vertical spacing, instead of
	   letting the horizontal gap between them collapse below a usable minimum. */
	.dialog-footer.two-default-actions {
		justify-content: space-between !important;
		column-gap: 1.25rem !important;
		row-gap: 1.25rem !important;
		padding-inline: 1rem;
	}
	/* LSP-18633: the cancel button deliberately keeps Vuetify's own symmetric inline padding.
	   LSP-17065 previously zeroed `padding-inline-start` here so the label lined up with the
	   footer's 1rem gutter. That reads fine on a `text` cancel button, but in the
	   two-default-actions case the button renders `outlined` - a visible pill - and the
	   one-sided padding pushed its label hard against the left border, off-centre. The
	   footer's own `padding-inline: 1rem` provides the gutter; the button stays standard. */

	.dialog-header.shadow-bottom {
		box-shadow: 0 6px 6px -6px rgba(0, 0, 0, 0.2);
	}
	.dialog-footer.shadow-top {
		box-shadow: 0 -6px 6px -6px rgba(0, 0, 0, 0.2);
	}

	.dialog-body-container {
		max-height: 50vh !important;
		overflow-y: auto;
		scroll-behavior: smooth;
	}

	.landscape-dialog-body-container {
		max-height: 40vh !important;
	}

	.full-height .dialog-body-container {
		max-height: 90vh !important;
	}
	.full-height .landscape-dialog-body-container {
		max-height: 80vh !important;
	}

	.v-btn {
		text-transform: none !important;
	}
	.dialog-body-container
		:deep(.v-field.v-field--active:not(.v-field--error):not(.v-field--disabled) > .v-field__prepend-inner > .v-icon) {
		color: rgb(var(--v-theme-primary)) !important;
		opacity: 1 !important;
	}
</style>
